import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Search
} from '@mui/icons-material';
import {
  Button,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import MasterModal from './MasterModal';
import Pagination from '../../common/Pagination';

const ClientIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, token, userId } = useAuth();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [clients, setClients] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});

  // Separate data states for Master, LedgerGroup, Ledger
  const [masterData, setMasterData] = useState({});
  const [ledgerGroupData, setLedgerGroupData] = useState({});
  const [ledgerData, setLedgerData] = useState({});

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingVariant, setLoadingVariant] = useState('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchClients();
    }
  }, [companyId, token]);

  const fetchClients = async () => {
    setLoading(true);
    setLoadingMessage('Fetching clients...');
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setClients(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
    setLoading(false);
  };

  const fetchSingleItem = async (id) => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${id}`, token);
      console.log('API Response:', response);

      let data = null;
      if (response.response_data) {
        data = response.response_data;
      } else if (response.Master || response.Ledger || response.LedgerGroup) {
        data = response;
      } else if (response.responseCode === '1111' && response.response_data) {
        data = response.response_data;
      }

      if (data) {
        return {
          master: data.Master || data.master || {},
          ledgerGroup: data.LedgerGroup || data.ledgerGroup || {},
          ledger: data.Ledger || data.ledger || {}
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching Client details:', error);
      return null;
    }
  };

  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);

    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;
    console.log('Opening modal for ID:', itemId, 'Action:', action);

    if ((action === 'view' || action === 'edit') && itemId) {
      setLoading(true);
      setLoadingMessage(`Fetching client details...`);

      const freshData = await fetchSingleItem(itemId);
      console.log('Fetched data:', freshData);

      if (freshData && (freshData.master?.Id || freshData.ledger?.Id)) {
        setMasterData(freshData.master);
        setLedgerGroupData(freshData.ledgerGroup);
        setLedgerData(freshData.ledger);

        const combinedData = {
          Id: freshData.master.Id,
          MasterId: freshData.master.Id,
          Name: freshData.master.Name || '',
          SearchableName: freshData.master.SearchableName || '',
          BalanceSheetHeadId: freshData.master.BalanceSheetHeadId,
          BalanceSheetHeadName: freshData.master.BalanceSheetHeadName || '',
          OpeningBalance: freshData.master.OpeningBalance ?? 0,
          OpeningDate: freshData.master.OpeningDate,
          IsPayableOrReceivable: freshData.master.IsPayableOrReceivable,
          IsActive: freshData.master.IsActive ?? true,
          LedgerGroupId: freshData.ledgerGroup.Id,
          LedgerGroupName: freshData.ledgerGroup.Name || '',
          GroupCreditDays: freshData.ledgerGroup.CreditDays ?? 0,
          GroupCreditLimit: freshData.ledgerGroup.CreditLimit ?? 0,
          LedgerId: freshData.ledger.Id,
          IsType: freshData.ledger.IsType,
          InvoiceTypeId: freshData.ledger.InvoiceTypeId ?? 0,
          SalesExecutiveId: freshData.ledger.SalesExecutiveId ?? 0,
          StateCodeId: freshData.ledger.StateCodeId,
          AddressLine1: freshData.ledger.AddressLine1 || '',
          AddressLine2: freshData.ledger.AddressLine2 || '',
          Location: freshData.ledger.Location || '',
          PinCode: freshData.ledger.PinCode || '',
          DeliveryAddressLine1: freshData.ledger.DeliveryAddressLine1 || '',
          DeliveryAddressLine2: freshData.ledger.DeliveryAddressLine2 || '',
          DeliveryLocation: freshData.ledger.DeliveryLocation || '',
          DeliveryPinCode: freshData.ledger.DeliveryPinCode || '',
          ContactNumber1: freshData.ledger.ContactNumber1 || '',
          ContactNumber2: freshData.ledger.ContactNumber2 || '',
          ContactNumber3: freshData.ledger.ContactNumber3 || '',
          Email: freshData.ledger.Email || '',
          VATNumber: freshData.ledger.VATNumber || '',
          PanNumber: freshData.ledger.PanNumber || '',
          MSMENumber: freshData.ledger.MSMENumber || '',
          CreditDays: freshData.ledger.CreditDays ?? 0,
          CreditLimit: freshData.ledger.CreditLimit ?? 0
        };

        console.log('Combined formData:', combinedData);
        setSelectedItem(combinedData);
        setFormData(combinedData);
      } else {
        console.log('Fetch failed, using fallback');
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
      }

      setLoading(false);
    } else {
      setMasterData({});
      setLedgerGroupData({});
      setLedgerData({});
      setSelectedItem(null);
      setFormData({});
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setMasterData({});
    setLedgerGroupData({});
    setLedgerData({});
    window.location.reload();
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} client...`);
      setLoadingVariant('default');

      const masterId = formData.Id || formData.MasterId || 0;
      const ledgerGroupId = formData.LedgerGroupId || 0;
      const ledgerId = formData.LedgerId || 0;

      const dataToSend = {
        Master: {
          Id: modalAction === 'edit' ? masterId : 0,
          CompanyId: companyId,
          BalanceSheetHeadId: formData.BalanceSheetHeadId || 0,
          MasterTypeId: formData.MasterTypeId || 1,
          Name: formData.Name || '',
          SearchableName: formData.SearchableName || formData.Name || '',
          OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
          OpeningDate: formData.OpeningDate ? formData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: formData.IsPayableOrReceivable ?? true,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? (userId || 0) : (masterData.CreatedBy || userId || 0),
          ModifiedBy: modalAction === 'edit' ? (userId || 0) : null,
          CreatedDate: modalAction === 'edit' ? masterData.CreatedDate : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        },
        LedgerGroup: {
          Id: modalAction === 'edit' ? ledgerGroupId : 0,
          CompanyId: companyId,
          MasterId: modalAction === 'edit' ? masterId : 0,
          Name: formData.LedgerGroupName || '',
          CreditDays: parseInt(formData.GroupCreditDays) || 0,
          CreditLimit: parseFloat(formData.GroupCreditLimit) || 0,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? (userId || 0) : (ledgerGroupData.CreatedBy || userId || 0),
          ModifiedBy: modalAction === 'edit' ? (userId || 0) : null,
          CreatedDate: modalAction === 'edit' ? ledgerGroupData.CreatedDate : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        },
        Ledger: {
          Id: modalAction === 'edit' ? ledgerId : 0,
          CompanyId: companyId,
          MasterId: modalAction === 'edit' ? masterId : 0,
          LedgerGroupId: modalAction === 'edit' ? ledgerGroupId : 0,
          IsType: formData.IsType || 1,
          InvoiceTypeId: formData.InvoiceTypeId || 0,
          SalesExecutiveId: formData.SalesExecutiveId || 0,
          StateCodeId: formData.StateCodeId || null,
          AddressLine1: formData.AddressLine1 || '',
          AddressLine2: formData.AddressLine2 || '',
          Location: formData.Location || '',
          PinCode: formData.PinCode || '',
          DeliveryAddressLine1: formData.DeliveryAddressLine1 || '',
          DeliveryAddressLine2: formData.DeliveryAddressLine2 || '',
          DeliveryLocation: formData.DeliveryLocation || '',
          DeliveryPinCode: formData.DeliveryPinCode || '',
          ContactNumber1: formData.ContactNumber1 || '',
          ContactNumber2: formData.ContactNumber2 || '',
          ContactNumber3: formData.ContactNumber3 || '',
          Email: formData.Email || '',
          VATNumber: formData.VATNumber || '',
          PanNumber: formData.PanNumber || '',
          MSMENumber: formData.MSMENumber || '',
          CreditDays: parseInt(formData.CreditDays) || 0,
          CreditLimit: parseFloat(formData.CreditLimit) || 0,
          OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
          OpeningDate: formData.OpeningDate ? formData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: formData.IsPayableOrReceivable ?? true,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? (userId || 0) : (ledgerData.CreatedBy || userId || 0),
          ModifiedBy: modalAction === 'edit' ? (userId || 0) : null,
          CreatedDate: modalAction === 'edit' ? ledgerData.CreatedDate : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
        }
      };

      console.log('Data to send:', dataToSend);

      if (modalAction === 'create') {
        await postData('ClientAndVendorApi', '', dataToSend, token);
        setSuccess(`Client created successfully!`);
      } else if (modalAction === 'edit') {
        if (!masterId) {
          throw new Error('Master ID is missing');
        }
        await updateData('ClientAndVendorApi', masterId, dataToSend, token);
        setSuccess(`Client updated successfully!`);
      }

      handleCloseModal();
      await fetchClients();
      setLoading(false);

    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} client. Please try again.`);
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (id, name) => {
    setDeleteItemId(id);
    setDeleteItemName(name);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemName('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('ClientAndVendorApi', deleteItemId, token);
      setSuccess(`Client deleted successfully!`);
      await fetchClients();
    } catch (error) {
      console.error('Error deleting:', error);
      setError(`Failed to delete client.`);
    }
    setLoading(false);
  };

  const getFilteredData = (data) => {
    let filtered = data;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ContactNumber1?.includes(searchTerm) ||
        item.Email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterActive !== 'all') {
      filtered = filtered.filter(item =>
        filterActive === 'active' ? item.IsActive : !item.IsActive
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: filtered.slice(startIndex, endIndex),
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchClients();
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(clients);

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {/* Header Section with Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Romans',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Client</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Search Bar and Create Button in Same Row */}
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, contact, email, VAT number..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#667eea', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />
          <Tooltip title="Create a new client" arrow>
            <Button
              variant="contained"
              onClick={() => handleOpenModal('create')}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                border: 'none',
                minHeight: '29px',
                textTransform: 'none',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <span style={{ fontSize: '12px' }}>+</span>
              Create Client
            </Button>
          </Tooltip>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <Table sx={{ minWidth: 650 }} aria-label="client table">
              <TableHead>
                <TableRow style={{
                  background: 'linear-gradient(135deg, #612261ff 0%, #764ba2 100%)',
                  height: '27px'
                }}>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Sl No.
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Name
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Ledger Group
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Contact
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    VAT Number
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Pin Code
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Type
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'right',
                    height: '27px'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.Id} style={{
                    background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                    borderBottom: '2px solid #f0f0f0'
                  }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {item.Name}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {item.LedgerGroupName || '-'}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {item.ContactNumber1 || '-'}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {item.VATNumber || '-'}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      {item.PinCode || '-'}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip
                        label={item.IsType === 1 ? 'Client' : item.IsType === 2 ? 'Supplier' : item.IsType === 3 ? 'Both' : '-'}
                        color={item.IsType === 3 ? 'primary' : item.IsType === 1 ? 'info' : item.IsType === 2 ? 'secondary' : 'default'}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip
                        label={item.IsActive ? 'Active' : 'Inactive'}
                        color={item.IsActive ? 'success' : 'default'}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details" arrow>
                          <Button
                            size="small"
                            onClick={() => handleOpenModal('view', item.MasterId)}
                            style={{
                              minWidth: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              color: 'white',
                              padding: '2px',
                              boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                              border: 'none',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}
                          >
                            👁️
                          </Button>
                        </Tooltip>

                        <Tooltip title="Edit Client" arrow>
                          <Button
                            size="small"
                            onClick={() => handleOpenModal('edit', item.MasterId)}
                            style={{
                              minWidth: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              color: 'white',
                              padding: '2px',
                              boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                              border: 'none',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}
                          >
                            ✏️
                          </Button>
                        </Tooltip>

                        <Tooltip title="Delete Client" arrow>
                          <Button
                            size="small"
                            onClick={() => handleOpenDeleteModal(item.MasterId, item.Name)}
                            style={{
                              minWidth: '22px',
                              height: '22px',
                              borderRadius: '6px',
                              color: 'white',
                              padding: '2px',
                              boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                              border: 'none',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}
                          >
                            🗑️
                          </Button>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination Section */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalCount={totalCount}
          startIndex={startIndex}
        />
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} variant={loadingVariant} />

      <MasterModal
        open={openModal}
        onClose={handleCloseModal}
        modalType="client"
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        loading={loading}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Delete Client
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete <strong style={{ color: '#1e293b' }}>{deleteItemName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteModal}
            variant="outlined"
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': { backgroundColor: '#b91c1c' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClientIndex;
